import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Button } from 'components/ui/Button/Button'
import { Flex, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, Head1 } from 'styling/Typography'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { CORE_STATE_META, SOURCE_META, taskCoreState } from 'pages/Operations/Shared/opsControl'
import { OPS_LIVE_TASKS, OPS_WORKERS } from 'pages/Operations/Shared/opsControlMocks'
import { opsDepartments } from 'pages/Operations/Shared/opsDepartments'
import { OpsCoreState, OpsDepartmentKey, OpsTaskSource } from 'types/operationsControl.interface'
import {
  IDepartmentSourceFilterOption,
  IDepartmentStatusFilterOption,
  IDepartmentTabOption,
} from 'types/departmentBoard.interface'
import { TaskQueueCard } from './TaskQueueCard'
import { TeamPanel } from './TeamPanel'
import { DepartmentContextPanels } from './ContextPanels'
import { BoardColumns, EmptyWrap, FilterBar, FilterGroup, FilterLabel, QueueColumn, SideColumn } from './DepartmentBoard.styled'

const DEPARTMENT_TABS: IDepartmentTabOption[] = opsDepartments().map((d) => ({ key: d.key, label: d.name }))

const STATUS_FILTERS: IDepartmentStatusFilterOption[] = [
  { key: 'ALL', label: 'All' },
  { key: 'DRAFT', label: 'Queued' },
  { key: 'ACTIVE', label: CORE_STATE_META.ACTIVE.label },
  { key: 'PAUSED', label: CORE_STATE_META.PAUSED.label },
  { key: 'BLOCKED', label: CORE_STATE_META.BLOCKED.label },
  { key: 'COMPLETED', label: CORE_STATE_META.COMPLETED.label },
]

const SOURCE_FILTERS: IDepartmentSourceFilterOption[] = [
  { key: 'ALL', label: 'All' },
  { key: 'PLAN', label: SOURCE_META.PLAN.label },
  { key: 'SHIFT_PROGRAM', label: SOURCE_META.SHIFT_PROGRAM.label },
  { key: 'TRIGGER', label: SOURCE_META.TRIGGER.label },
  { key: 'MANUAL', label: SOURCE_META.MANUAL.label },
]

export const DepartmentBoard = () => {
  const navigate = useNavigate()
  const [department, setDepartment] = useState<OpsDepartmentKey>(DEPARTMENT_TABS[0]?.key ?? '')
  const [statusFilter, setStatusFilter] = useState<'ALL' | OpsCoreState>('ALL')
  const [sourceFilter, setSourceFilter] = useState<'ALL' | OpsTaskSource>('ALL')
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const departmentLabel = DEPARTMENT_TABS.find((d) => d.key === department)?.label ?? ''

  const tasks = OPS_LIVE_TASKS.filter(
    (t) =>
      t.department === department &&
      (statusFilter === 'ALL' || taskCoreState(t.taskStatus) === statusFilter) &&
      (sourceFilter === 'ALL' || t.source === sourceFilter),
  )

  const selectDepartment = (key: OpsDepartmentKey) => {
    setDepartment(key)
    setExpandedId(null)
  }

  if (!DEPARTMENT_TABS.length) {
    return (
      <>
        <NavBar />
        <PageWrapper>
          <Flex center gap='0.75rem'>
            <Head1>Department board</Head1>
            <DemoBadge />
          </Flex>
          <NoData text='Departments will appear here once the HR department registry is connected.' />
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex center gap='0.75rem'>
          <Head1>Department board</Head1>
          <DemoBadge />
        </Flex>
        <Flex mt='16'>
          <TabsContainer>
            {DEPARTMENT_TABS.map((d) => (
              <Tabs key={d.key} active={department === d.key} onClick={() => selectDepartment(d.key)}>
                {d.label}
              </Tabs>
            ))}
          </TabsContainer>
        </Flex>
        <FilterBar>
          <FilterGroup>
            <FilterLabel>Status</FilterLabel>
            <TabsContainer>
              {STATUS_FILTERS.map((f) => (
                <Tabs key={f.key} active={statusFilter === f.key} onClick={() => setStatusFilter(f.key)}>
                  {f.label}
                </Tabs>
              ))}
            </TabsContainer>
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>Source</FilterLabel>
            <TabsContainer>
              {SOURCE_FILTERS.map((f) => (
                <Tabs key={f.key} active={sourceFilter === f.key} onClick={() => setSourceFilter(f.key)}>
                  {f.label}
                </Tabs>
              ))}
            </TabsContainer>
          </FilterGroup>
        </FilterBar>
        <BoardColumns>
          <QueueColumn>
            {tasks.length ? (
              tasks.map((t) => (
                <TaskQueueCard
                  key={t.id}
                  task={t}
                  expanded={expandedId === t.id}
                  onToggle={() => setExpandedId(expandedId === t.id ? null : t.id)}
                />
              ))
            ) : (
              <EmptyWrap>
                <BodyText grey>No tasks for {departmentLabel} this shift.</BodyText>
                <Button variant='primary' onClick={() => navigate('/operations/tasks')}>
                  Create manual task
                </Button>
              </EmptyWrap>
            )}
          </QueueColumn>
          <SideColumn>
            <TeamPanel workers={OPS_WORKERS} />
            <DepartmentContextPanels department={department} />
          </SideColumn>
        </BoardColumns>
      </PageWrapper>
    </>
  )
}
